import React from "react";

import Box from "@/ui/atoms/Box";

import type { ICellRendererParams } from "@/ui/molecules/Grid";

import type {
    TAllRowsAvailable,
    TRowMappingsWithOperatingStatements,
} from "../../typesGrids";
import { isRowCategory, isRowTotal } from "../../typeChecksGrids";
import { getPinnedRowBackgroundColor } from "../../utils";
import { Icon, IconBox, Tooltip } from "@proda-ai/ui-kit";

const cellStylesWithEllipsis = {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    display: "block",
};

type GridMappingsRendererFirstColumnProps =
    ICellRendererParams<TAllRowsAvailable> & {
        isTypeAltered: boolean;
        shouldShowBorder: boolean;
    };

const GridMappingsRendererFirstColumn: React.FC<
    GridMappingsRendererFirstColumnProps
> = params => {
    const { data, isTypeAltered, shouldShowBorder } = params;

    if (!data) return null;

    // @FIXME these checks below are only valid for mapping grid
    const isNonOperating =
        (data as TRowMappingsWithOperatingStatements).currentSectionType ===
        "non-operating";
    const isOmitted =
        (data as TRowMappingsWithOperatingStatements).mappingType === "omit";
    const shouldUseGreyedOutColor = isNonOperating || isOmitted;

    const pinnedRowBackgroundColor = getPinnedRowBackgroundColor(data);

    return isRowTotal(data) ? (
        <Box
            sx={{
                backgroundColor:
                    pinnedRowBackgroundColor ?? "var(--proda-white-l95)",
                fontWeight: "bold",
                textAlign: "right",
                paddingLeft: "16px",
                paddingRight: "16px",
                ...cellStylesWithEllipsis,
            }}
        >
            {isTypeAltered && (
                <Tooltip
                    content="This row type was altered"
                    placement="left-end"
                >
                    <IconBox
                        style={{
                            position: "absolute",
                            top: "-5px",
                            left: "-2px",
                            transform: "rotate(135deg)",
                        }}
                    >
                        <Icon
                            icon="caret-down"
                            size="lg"
                            color={"var(--proda-purple)"}
                        />
                    </IconBox>
                </Tooltip>
            )}
            {data.totalName}
        </Box>
    ) : isRowCategory(data) ? (
        <Box
            sx={{
                backgroundColor: "var(--proda-white-130)",
                fontWeight: "bold",
                paddingLeft: "16px",
                paddingRight: "16px",
                ...cellStylesWithEllipsis,
            }}
        >
            {isTypeAltered && (
                <Tooltip
                    content="This row type was altered"
                    placement="left-end"
                >
                    <IconBox
                        style={{
                            position: "absolute",
                            top: "-5px",
                            left: "-2px",
                            transform: "rotate(135deg)",
                        }}
                    >
                        <Icon
                            icon="caret-down"
                            size="lg"
                            color={"var(--proda-purple)"}
                        />
                    </IconBox>
                </Tooltip>
            )}
            {data.categoryName}
        </Box>
    ) : (
        <Box
            sx={{
                paddingLeft: "16px",
                paddingRight: "16px",
                color: shouldUseGreyedOutColor
                    ? "var(--text-color-disabled)"
                    : "var(--proda-black)",
                ...{
                    borderLeft: shouldShowBorder
                        ? data.mappingSource === "labelset"
                            ? "8px solid var(--proda-teal)"
                            : "8px solid var(--proda-navy-blue)"
                        : "none",
                },
                ...cellStylesWithEllipsis,
            }}
        >
            {isTypeAltered && (
                <Tooltip
                    content="This row type was altered"
                    placement="right-end"
                >
                    <IconBox
                        style={{
                            position: "absolute",
                            top: "-5px",
                            right: "-2px",
                            transform: "rotate(-135deg)",
                        }}
                    >
                        <Icon
                            icon="caret-down"
                            size="lg"
                            color={"var(--proda-purple)"}
                        />
                    </IconBox>
                </Tooltip>
            )}
            {data.originalAccountName}
        </Box>
    );
};

export default GridMappingsRendererFirstColumn;
